import { setToken, setRefreshToken, setUserData, setMobileApp } from './auth';
import { parseJwt } from './auth';

/**
 * Handle mobile app authentication via URL hash parameters
 * This is called when the Flutter app passes tokens in the URL
 *
 * URL Format: https://yourapp.com#access_token=xxx&refresh_token=yyy&expiration=zzz
 */
export function handleMobileAppAuth() {
  const hash = window.location.hash;

  // Check if we have authentication parameters in the URL hash
  if (!hash || hash.length <= 1) {
    return false;
  }

  try {
    // Debug: Log the raw hash
    console.log('[Mobile Auth] Raw URL hash:', hash);

    // Parse hash manually to avoid URLSearchParams auto-decoding
    // This preserves %20 in tokens instead of converting to space
    const hashParams = hash.substring(1);
    const params = {};

    hashParams.split('&').forEach(param => {
      const equalIndex = param.indexOf('=');
      if (equalIndex > 0) {
        const key = decodeURIComponent(param.substring(0, equalIndex));
        const value = param.substring(equalIndex + 1); // Keep value as-is, don't decode
        params[key] = value;
      }
    });

    let accessToken = params['access_token'];
    let refreshToken = params['refresh_token'];
    const expiration = params['expiration'];

    // Clean up tokens: remove any %20 encoding that shouldn't be there
    // This happens when tokens are URL-encoded but shouldn't contain encoded spaces
    if (accessToken) {
      accessToken = accessToken.replace(/%20/g, '');
    }
    if (refreshToken) {
      refreshToken = refreshToken.replace(/%20/g, '');
    }

    // Debug: Log what we extracted
    console.log('[Mobile Auth] Parsed tokens:', {
      accessToken: accessToken?.substring(0, 20) + '...',
      refreshToken: refreshToken,
      refreshTokenLength: refreshToken?.length,
      cleanedToken: true,
      expiration: expiration
    });

    // Validate that we have required tokens
    if (!accessToken || !refreshToken) {
      console.log('[Mobile Auth] No tokens found in URL hash');
      return false;
    }

    console.log('[Mobile Auth] Tokens received from mobile app');

    // Calculate expiration time (default to 60 minutes if not provided)
    let expirationTime;
    if (expiration) {
      expirationTime = expiration;
    } else {
      // Default: 60 minutes from now
      expirationTime = new Date(Date.now() + 60 * 60 * 1000).toISOString();
    }

    // Parse access token to get user data
    const tokenPayload = parseJwt(accessToken);
    let userData = null;

    if (tokenPayload) {
      // Extract user data from token
      userData = {
        email: tokenPayload.email || tokenPayload.sub || tokenPayload.unique_name,
        roles: tokenPayload.role || tokenPayload.roles || [],
      };

      // Ensure roles is an array
      if (!Array.isArray(userData.roles)) {
        userData.roles = [userData.roles];
      }
    }

    // Store tokens and user data in cookies
    setToken(accessToken, expirationTime);
    setRefreshToken(refreshToken);

    if (userData) {
      setUserData(userData);
    }

    // Store mobile app flag in cookie (persists across page refreshes)
    setMobileApp(true);

    // IMPORTANT: Update Vuex store with the authentication data
    // This is needed because the store initializes before mobile auth runs
    // Import store dynamically to avoid circular dependencies
    import('../store').then(({ default: store }) => {
      store.commit('user/SET_AUTH_DATA', {
        accessToken,
        refreshToken,
        accessTokenExpiration: expirationTime,
        userData,
        isMobileApp: true // Mark that user is accessing from mobile app
      });
      console.log('[Mobile Auth] Vuex store updated with auth data (Mobile App Mode)');
    }).catch(err => {
      console.error('[Mobile Auth] Error updating Vuex store:', err);
    });

    // Clear the hash from URL for security (prevent token exposure in history)
    // Use replaceState to avoid adding to browser history
    if (window.history && window.history.replaceState) {
      const cleanUrl = window.location.href.split('#')[0];
      window.history.replaceState(null, '', cleanUrl);
    } else {
      window.location.hash = '';
    }

    console.log('[Mobile Auth] Authentication successful, tokens stored');

    // Dispatch custom event to notify app that mobile auth is complete
    window.dispatchEvent(new CustomEvent('mobile-auth-complete', {
      detail: { userData }
    }));

    return true;
  } catch (error) {
    console.error('[Mobile Auth] Error processing mobile authentication:', error);
    return false;
  }
}

/**
 * Check if the app is running inside a mobile WebView
 */
export function isMobileApp() {
  // Check for Flutter WebView JavaScript channel
  if (typeof window.FlutterChannel !== 'undefined') {
    return true;
  }

  // Additional checks for mobile user agents
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Check if running in WebView (not full browser)
  const isWebView = (
    // Android WebView
    /wv/.test(userAgent) ||
    // iOS WebView
    /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent)
  );

  return isWebView;
}

/**
 * Send message to Flutter app
 */
export function sendMessageToFlutter(message) {
  if (window.FlutterChannel && window.FlutterChannel.postMessage) {
    try {
      window.FlutterChannel.postMessage(JSON.stringify(message));
      console.log('[Mobile Bridge] Message sent to Flutter:', message);
    } catch (error) {
      console.error('[Mobile Bridge] Error sending message to Flutter:', error);
    }
  }
}

/**
 * Notify Flutter app about logout
 */
export function notifyMobileAppLogout() {
  if (isMobileApp()) {
    sendMessageToFlutter({ type: 'logout' });
  }
}

/**
 * Notify Flutter app about token refresh
 */
export function notifyMobileAppTokenRefresh(accessToken, refreshToken, expiration) {
  if (isMobileApp()) {
    sendMessageToFlutter({
      type: 'token-refresh',
      data: {
        accessToken,
        refreshToken,
        expiration
      }
    });
  }
}

/**
 * Listen for messages from Flutter app
 */
export function setupMobileAppListeners() {
  // Listen for mobile auth ready event from Flutter
  window.addEventListener('mobile-auth-ready', () => {
    console.log('[Mobile Bridge] Mobile app is ready');
  });

  // Listen for visibility changes (app going to background/foreground)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && isMobileApp()) {
      console.log('[Mobile Bridge] App became visible');
      // You can trigger token validation here if needed
    }
  });
}
